import { createSlice } from "@reduxjs/toolkit";
import { roles, verificationStatus } from "../../constants/commonConstants";
import { signin, updateUserProfile } from "../thunks/authThunks";

// Load authentication state from localStorage (if exists)
const storedAuth = JSON.parse(localStorage.getItem("auth")) || {};

const initialState = {
  isAuthenticated: storedAuth.isAuthenticated || false,
  user: storedAuth.user || null,
  role: storedAuth.role || roles.USER,
  isSessionExpired: false,
  verificationStatus:
    storedAuth.verificationStatus || verificationStatus.PENDING,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    // logout
    logout: (state) => {
      state.isAuthenticated = false;
      state.isSessionExpired = false;
      state.verificationStatus = null;
      state.user = null;
      state.role = roles.USER;
      localStorage.removeItem("token");
      localStorage.removeItem("auth");
      localStorage.setItem("auth", JSON.stringify({ role: roles.USER }));
      
      // Clear preview tracking data
      sessionStorage.removeItem("previewTrackCount");
      sessionStorage.removeItem("subscriptionModalShown");
    },

    // handle session expiration
    handleSessionExpiration: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.role = roles.USER;
      state.verificationStatus = null;
      state.isSessionExpired = true;
      localStorage.removeItem("token");
      localStorage.removeItem("auth");
      localStorage.setItem("auth", JSON.stringify({ role: roles.USER }));
      
      // Clear preview tracking data
      sessionStorage.removeItem("previewTrackCount");
      sessionStorage.removeItem("subscriptionModalShown");
    },

    // handle user email update

    handleUserEmailUpdate: (state, action) => {
      if (state.user && action.payload?.email) {
        const newEmail = action.payload.email.toLowerCase().trim();
        state.user.email = newEmail;

        // Update localStorage more safely
        try {
          const storedAuth = JSON.parse(localStorage.getItem("auth")) || {};
          if (storedAuth.user) {
            storedAuth.user.email = newEmail;
            localStorage.setItem("auth", JSON.stringify(storedAuth));
          }
        } catch (error) {
          console.error("Failed to update email in localStorage:", error);
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(signin.fulfilled, (state, action) => {
      handleAuthSuccess(state, action.payload.data);
    });

    builder.addCase(updateUserProfile.fulfilled, (state, action) => {
      const {
        firstName,
        lastName,
        email,
        djName,
        djType,
        subscribeToMailingList,
      } = action.payload.data;

      if (state.user) {
        Object.assign(state.user, {
          firstName,
          lastName,
          email,
          djName,
          djType,
          subscribeToMailingList,
        });
      } else {
        state.user = {
          firstName,
          lastName,
          email,
          djName,
          djType,
          subscribeToMailingList,
        };
      }

      // Update localStorage efficiently
      const storedAuth = JSON.parse(localStorage.getItem("auth")) || {};
      storedAuth.user = {
        ...(storedAuth.user || {}),
        firstName,
        lastName,
        email,
        djName,
        djType,
        subscribeToMailingList,
      };
      localStorage.setItem("auth", JSON.stringify(storedAuth));
    });
  },
});

// Helper function to decode JWT token
const decodeToken = (token) => {
  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch (error) {
    console.error("Error decoding token:", error);
    return {};
  }
};

// handle authentication success
const handleAuthSuccess = (state, data) => {
  const { userData, token } = data;

  const decodedToken = decodeToken(token);
  const userVerificationStatus = decodedToken?.verificationStatus || null;

  state.isAuthenticated = true;
  state.user = userData;
  state.role = state.role || roles.USER;
  state.verificationStatus = userVerificationStatus;

  // Save token separately so api.js interceptor can attach it to all requests
  localStorage.setItem("token", token);

  localStorage.setItem(
    "auth",
    JSON.stringify({
      isAuthenticated: true,
      user: userData,
      role: state.role,
      verificationStatus: userData.verificationStatus,
    }),
  );
};

export const { logout, handleSessionExpiration, handleUserEmailUpdate } =
  authSlice.actions;
export default authSlice.reducer;
